import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TurnstileLoaderService } from './turnstile-loader.service';
import { TurnstileRetry, TurnstileSize, TurnstileTheme } from './turnstile.types';

@Component({
  selector: 'ef-turnstile',
  standalone: true,
  template: '<div #container class="w-full"></div>',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EfTurnstileComponent),
      multi: true,
    },
  ],
  exportAs: 'efTurnstile',
})
export class EfTurnstileComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  private static readonly CHANGE_NOOP = (_value: string | null) => undefined;
  private static readonly TOUCH_NOOP = () => undefined;

  @Input({ required: true }) public siteKey!: string;
  @Input() public size: TurnstileSize = 'normal';
  @Input() public theme: TurnstileTheme = 'light';
  @Input() public retry: TurnstileRetry = 'auto';
  @Input() public tabindex = 0;
  @Input() public action = 'submit';

  @Output() public readonly resolved = new EventEmitter<string | null>();

  @ViewChild('container', { static: true })
  private readonly container!: ElementRef<HTMLDivElement>;

  private onChange: (value: string | null) => void = EfTurnstileComponent.CHANGE_NOOP;
  private onTouched: () => void = EfTurnstileComponent.TOUCH_NOOP;
  private widgetId?: string;
  private lastValue: string | null = null;
  private pendingReset = false;
  private destroyed = false;

  constructor(
    private readonly loader: TurnstileLoaderService,
    private readonly zone: NgZone,
  ) {}

  public async ngAfterViewInit(): Promise<void> {
    try {
      await this.loader.load();
      if (this.destroyed) return;
      this.renderWidget();
    } catch (error) {
      console.error('Failed to initialize Cloudflare Turnstile widget.', error);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    if (this.widgetId && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public writeValue(value: string | null): void {
    if (value === this.lastValue) {
      return;
    }

    if (!value) {
      this.triggerReset();
    }
  }

  public setDisabledState(_isDisabled: boolean): void {
    // Cloudflare Turnstile does not expose a dedicated disabled state.
  }

  public execute(): void {
    if (this.size !== 'invisible') {
      return;
    }

    if (window.turnstile && this.widgetId) {
      window.turnstile.execute(this.widgetId);
    }
  }

  public reset(): void {
    if (window.turnstile && this.widgetId) {
      window.turnstile.reset(this.widgetId);
      this.emitValue(null);
    } else {
      this.pendingReset = true;
    }
  }

  private renderWidget(): void {
    const turnstile = window.turnstile;

    if (!turnstile) {
      throw new Error('Cloudflare Turnstile script is not available on window.');
    }

    this.zone.runOutsideAngular(() => {
      this.widgetId = turnstile.render(this.container.nativeElement, {
        sitekey: this.siteKey,
        theme: this.theme,
        retry: this.retry,
        size: this.size,
        tabindex: this.tabindex,
        action: this.action,
        callback: (token) => this.zone.run(() => this.handleSuccess(token)),
        'expired-callback': () => this.zone.run(() => this.handleExpired()),
        'error-callback': () => this.zone.run(() => this.handleError()),
      });

      if (this.pendingReset) {
        this.pendingReset = false;
        this.reset();
      }
    });
  }

  private handleSuccess(token: string): void {
    this.emitValue(token);
  }

  private handleExpired(): void {
    this.emitValue(null);
  }

  private handleError(): void {
    this.emitValue(null);
  }

  private emitValue(value: string | null): void {
    this.lastValue = value;
    this.onChange(value);
    this.onTouched();
    this.resolved.emit(value);
  }

  private triggerReset(): void {
    if (this.widgetId && window.turnstile) {
      this.reset();
    } else {
      this.pendingReset = true;
    }
  }
}
